<template>
    <div>
        <transition name="vue-table-loader">
            <div class="vue-details-loader alt" v-if="loading">
                <span class="fa fa-cog fa-spin fa-1x"></span>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.deleteAssetModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.deleteAssetModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <div class="row mx-4 pb-1">
                        <p class="font-weight-bold">
                            Are you sure you want to delete this asset?
                        </p>
                    </div>
                    <div class="row mx-4 my-0 pb-0">
                        <div class="col-md-6 d-flex align-items-center">
                            <button class="btn my-2 btn-outline-primary" @click="uiStates.deleteAssetModal = false">
                                Cancel
                            </button>
                            <button class="btn ml-2 btn-danger" :disabled="loading" @click="confirmDeleteAsset">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.bulkDeleteModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.bulkDeleteModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <div class="row mx-4 pb-1 d-flex flex-column" style="min-width: 16rem; max-width: 20rem; width: 100%;">
                        <p class="font-weight-bold">
                            Are you sure you want to delete these assets?
                        </p>
                        <p class="text-muted mt-2" v-if="!selectedItems.length">
                            No items selected
                        </p>
                        <p class="text-muted mt-2" v-if="selectedItems.length > 3">
                            Delete {{ selectedItems[0].name }}, {{ selectedItems[1].name }} & {{ selectedItems.length - 2 }} others
                        </p>
                        <p class="text-muted mt-2" v-else>
                            Delete 
                            <span v-for="(item, index) in selectedItems" :key="item.id">
                                {{ index < selectedItems.length - 2 ? `${item.name},` : index < selectedItems.length - 1  ? `${item.name}` : `& ${item.name}` }}
                            </span>
                        </p>
                    </div>
                    <div class="row mx-4 my-0 pb-0 d-flex flex-column">
                        <div class="d-flex align-items-center p-0">
                            <button class="btn btn-outline-primary" @click="uiStates.bulkDeleteModal = false">
                                Cancel
                            </button>
                            <button data-id="delete-modal-submit" class="btn ml-2 btn-danger" :disabled="loading" @click="confirmBulkDelete">
                                Delete
                            </button>
                        </div>
                        <p class="text-danger mt-2" style="max-width: 46ch;">
                            <strong>Warning!</strong> Asset will be removed from all linked products/brands/categories.
                        </p>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.bulkAssignModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.bulkAssignModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <div class="row mx-4 pb-1">
                        <p class="font-weight-bold">
                            Assign selected products using the dropdown below
                        </p>
                    </div>
                    <div class="row mx-4 my-0 pb-0">
                        <div class="d-flex flex-column">
                            <div class="d-flex">
                                <select data-id="assign-type-select" class="form-select form-control"
                                    v-model="selectedLinkType"
                                >
                                    <option>Brands</option>
                                    <option>Products</option>
                                    <option>Categories</option>
                                </select>
                            </div>
                            <transition name="fade">
                                <div class="d-flex flex-column">
                                    <select-item-from-api-provided-list
                                        v-if="selectedLinkType === 'Products'"
                                        :api="'pimapi/products'"
                                        :api-type="'meta'"
                                        :list-label="'Select Product'"
                                        :columns="['name']"
                                        :results-per-page="8"
                                        small
                                        ref="selectFromApi"
                                        @selection="handleLinkSelection"
                                        :style="{ width: '24rem '}"
                                        key="productsSelection"
                                    >
                                    </select-item-from-api-provided-list>
                                    <select-item-from-api-provided-list
                                        v-else-if="selectedLinkType === 'Brands'"
                                        :api="'brands/grid'"
                                        :api-type="'grid'"
                                        :list-label="'Select Brand'"
                                        :columns="['name']"
                                        :results-per-page="8"
                                        small
                                        ref="selectFromApi"
                                        @selection="handleLinkSelection"
                                        :style="{ width: '24rem '}"
                                        key="brandsSelection"
                                    >
                                    </select-item-from-api-provided-list>
                                    <select-item-from-api-provided-list
                                        v-else-if="selectedLinkType === 'Categories'"
                                        :api="'categories/grid'"
                                        :api-type="'grid'"
                                        :list-label="'Select Category'"
                                        :columns="['name']"
                                        :results-per-page="8"
                                        small
                                        ref="selectFromApi"
                                        @selection="handleLinkSelection"
                                        :style="{ width: '24rem '}"
                                        key="categoriesSelection"
                                    >
                                    </select-item-from-api-provided-list>
                                    <div class="d-flex flex-column">
                                        <button class="btn btn-primary mt-2" @click="handleLinkAssets" :disabled="linkLoading">
                                            Attach Selected
                                        </button>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.bulkTagsModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.bulkTagsModal = false"></div>
                <div class="d-flex flex-column modal-inner p-4">
                    <div class="row mx-4 my-0 pb-0">
                        <div class="row w-100 pb-0 col-md-12 mx-auto">
                            <div class="col-md-12 p-0 d-flex flex-column">
                                <label for="published">Add Tags</label>
                                <div>
                                    <input data-id="tags-modal-input" type="text" name="" id="" ref="tag" class="form-control" v-model="pendingTag" @keypress.enter="addPendingTag">
                                </div>
                                <div class="text-muted mt-1" style="max-width: 46ch;">
                                    To submit multiple tags at once, press the 'Enter' key after entering the desired tag value
                                </div>
                                <div>
                                    <div class="asset-tag-cloud mt-4" data-id="tags-modal-pending">
                                        <button class="badge badge-primary pending-tag" v-for="tag in addTagsBulk" :key="tag" @click="removePendingTag(tag)">
                                            {{ tag }}
                                        </button>
                                    </div>
                                    <div v-if="addTagsBulk.length" class="text-muted mt-1">
                                        Click to remove pending tags
                                    </div>
                                    <button data-id="tags-modal-submit" class="btn btn-primary mt-3" :disabled="!addTagsBulk.length && !pendingTag.length" @click="submitTagsBulk">
                                        Submit
                                    </button>
                                </div>
                            </div>
                            <div class="col-md-12 px-0 d-flex flex-column mt-4" v-if="Object.keys(selectedMatchingTags).length">
                                <label for="published">Remove Tags</label>
                                <div class="d-flex flex-column p-2" style="overflow-y: auto; max-height: 28rem; border: 1px solid #dedede;">
                                    <div v-for="(tagItems, tag, index) in selectedMatchingTags" :key="tag" data-id="tags-modal-existing">
                                        <div class="d-flex align-items-center justify-content-between mb-3" :class="{ 'mt-3': index > 0 }">
                                            <span>
                                                <strong>{{ tag }}</strong>
                                            </span>
                                            <button class="btn btn-sm btn-danger" @click="removeTagBulk(tag, tagItems)">
                                                Remove Tag
                                            </button>
                                        </div>
                                        <ul style="list-style-type: none;">
                                            <li v-for="(item, index) in tagItems" :key="item.name" :class="{ 'mt-2': index > 0}">
                                                {{ item.name }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.bulkResizeModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.bulkResizeModal = false"></div>
                <div class="modal-inner p-4">
                    <div class="row mx-4 pb-1">
                        <p class="font-weight-bold">
                            Resize According to Specified Dimensions
                        </p>
                    </div>
                    <div class="row mx-4 my-0 pb-0 d-flex flex-column">
                        <div class="d-flex flex-column" style="min-width: 16rem;">
                            <div>
                                <label for="">
                                    Suffix
                                </label>
                                <input data-id="resize-modal-suffix" type="text" name="" id="" class="form-control mb-2" v-model="resizeSuffix" max="100" min="1">
                                <p class="text-muted mb-2 mt-1" style="font-size: 12px;">
                                    Suffix will be added to end of file name to differentiate from original
                                </p>
                            </div>
                            <div>
                                <label for="">
                                    Quality
                                </label>
                                <input data-id="resize-modal-quality" type="number" name="" id="" class="form-control mb-2" v-model="resizeQuality" max="100" min="1">
                            </div>
                            <div class="d-flex justify-content-between">
                                <div class="col-md-4 pl-0">
                                    <label for="">
                                        Use Percentage
                                    </label>
                                    <input type="checkbox" name="" id="" class="form-control mb-2" v-model="resizeUsePercent" style="width: 38px;">
                                </div>
                                <div class="col-md-4 pl-0">
                                    <label for="">
                                        Keep Aspect Ratio
                                    </label>
                                    <input data-id="resize-modal-keep-aspect" type="checkbox" name="" id="" class="form-control mb-2" :disabled="resizeUsePercent" v-model="resizeAspectRatio" style="width: 38px;">
                                </div>
                                <div class="col-md-4 pl-0">
                                    <label for="">
                                        Preserve
                                    </label>
                                    <select data-id="resize-modal-select-aspect" name="" id="" class="form-control" :disabled="!resizeAspectRatio || resizeUsePercent" v-model="resizeAspectRatioPreserve">
                                        <option value="width">Width</option>
                                        <option value="height">Height</option>
                                    </select>
                                </div>
                            </div>
                            <div class="d-flex">
                                <div class="col-md-4 pl-0">
                                    <label for="">
                                        Height (Pixels)
                                    </label>
                                    <input data-id="resize-modal-height" type="number" name="" id="" class="form-control mb-2" v-model="resizeHeight" :disabled="resizeUsePercent || (resizeAspectRatio && resizeAspectRatioPreserve === 'width')">
                                </div>
                                <div class="col-md-4 pl-0">
                                    <label for="">
                                        Width (Pixels)
                                    </label>
                                    <input type="number" name="" id="" class="form-control mb-2" v-model="resizeWidth" :disabled="resizeUsePercent || (resizeAspectRatio && resizeAspectRatioPreserve === 'height')">
                                </div>
                                <div class="col-md-4 pl-0">
                                    <label for="">
                                        Resize %
                                    </label>
                                    <input type="number" name="" id="" :disabled="!resizeUsePercent" class="form-control mb-2" v-model="resizePercent" min="1" max="200">
                                </div>
                            </div>
                        </div>  
                        <button data-id="resize-modal-submit" class="btn btn-primary mt-3" @click="submitResize">
                            Submit
                        </button>
                        <p class="text-danger mt-2" style="max-width: 46ch;">
                            <strong>Warning!</strong> Resizing photos in a way that alters the aspect 
                            ratio may result in unwanted stretching and artefacts.
                        </p>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.bulkWatermarkModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.bulkWatermarkModal = false"></div>
                <div class="modal-inner p-4">
                    <div class="row mx-4 pb-1">
                        <p class="font-weight-bold">
                            Add watermark to image assets
                        </p>
                    </div>
                    <div class="row mx-4 my-0 pb-0 d-flex flex-column">
                        <div class="d-flex flex-column" style="min-width: 16rem;">
                            <div>
                                <label for="">
                                    Suffix
                                </label>
                                <input data-id="watermark-modal-suffix" type="text" name="" id="" class="form-control mb-2" v-model="watermarkSuffix" max="100" min="1">
                                <p class="text-muted mb-2 mt-1" style="font-size: 12px;">
                                    Suffix will be added to end of file name to differentiate from original
                                </p>
                            </div>
                            <div class="d-flex flex-column">
                                <label for="">
                                    Position                                    
                                </label>
                                <select data-id="watermark-modal-position" class="mb-2 form-control" v-model="watermarkPosition">
                                    <option value="top-left">Top Left</option>
                                    <option value="top-right">Top Right</option>
                                    <option value="bottom-left">Bottom Left</option>
                                    <option value="bottom-right">Bottom Right</option>
                                    <option value="center">Center</option>
                                </select>
                            </div>
                            <div class="d-flex flex-column">
                                <label for="">
                                    Watermark Image
                                </label>
                                <input data-id="watermark-modal-image" type="file" name="" id="" ref="watermark" class="mb-2">
                            </div>
                        </div>  
                        <button data-id="watermark-modal-submit" class="btn btn-primary mt-3" @click="submitWatermark">
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.bulkConvertModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.bulkConvertModal = false"></div>
                <div class="modal-inner p-4">
                    <div class="row mx-4 pb-1">
                        <p class="font-weight-bold">
                            Bulk Convert Images
                        </p>
                    </div>
                    <div class="row mx-4 my-0 pb-0 d-flex flex-column">
                        <div>
                            <label for="">
                                Suffix
                            </label>
                            <input data-id="convert-modal-suffix" type="text" name="" id="" class="form-control" v-model="convertSuffix" max="100" min="1">
                            <p class="text-muted mb-2 mt-1" style="font-size: 12px;">
                                Suffix will be added to end of file name to differentiate from original
                            </p>
                        </div>
                        <div class="d-flex flex-column" style="min-width: 16rem;">
                            <label for="">
                                Format
                            </label>
                            <select data-id="convert-modal-type" v-model="convertFormat" class="form-control">
                                <option value="jpeg">jpeg</option>
                                <option value="png">png</option>
                                <option value="webp">webp</option>
                            </select>
                        </div>
                        <button data-id="convert-modal-submit" class="btn btn-primary mt-3" @click="submitConvert">
                            Submit
                        </button>
                        <p class="text-muted mt-2" style="max-width: 46ch;">
                            Converted assets will be saved as new asset versions
                        </p>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div v-if="uiStates.bulkFolderModal" class="modal-wrapper">
                <div class="modal-bg" @click="uiStates.bulkFolderModal = false"></div>
                <div class="modal-inner p-4">
                    <div class="row mx-4 pb-1">
                        <p class="font-weight-bold">
                            Assign To Folder
                        </p>
                    </div>
                    <div class="row mx-4 my-0 pb-0 d-flex flex-column">
                        <div>
                            <label for="">
                                Folder
                            </label>
                        </div>
                        <div>
                            <select-folder data-id="folder-modal-select" @selection="handleFolderSelection" :current="root ? 0 : currentFolder.id"></select-folder>
                        </div>
                        <button data-id="folder-modal-submit" class="btn btn-primary mt-3" @click="submitSetFolder">
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="fade">
            <div>
                <vuetable
                    data-id="asset-list-table"
                    ref="vuetable"
                    :fields="fields"
                    :api-url="apiURL"
                    :css="css.table"
                    :per-page="perPage"
                    :append-params="moreParams"
                    noDataTemplate="No Assets Found"
                    pagination-path=""
                    @vuetable:pagination-data="onPaginationData"
                    @vuetable:loaded="onLoaded"
                    @vuetable:checkbox-toggled="onCheckbox"
                    @vuetable:checkbox-toggled-all="onCheckboxAll"
                >
                    <template
                        slot="thumbnail-slot"
                        slot-scope="props"
                        class="asset-items__thumbnail-wrapper"
                    >
                        <img v-if="assetTypesMap.displayableImageTypes.includes(props.rowData.asset_type_id) && props.rowData.uri_internal" 
                            :src="props.rowData.uri_internal"
                            alt=""
                            class="asset-items__thumbnail"
                        />
                        <div v-else class="asset-preview__icon-outer" @click="lightbox = true" >
                            <div class="asset-preview__icon-inner d-flex align-items-center justify-content-center">
                                <i v-if="assetTypesMap.imagePlaceholderTypes.includes(props.rowData.asset_type_id)" 
                                    class="fa fa-file-image-o fa-2x text-light" 
                                    aria-hidden="true"
                                ></i>
                                <i v-else-if="assetTypesMap.displayableVideoTypes.includes(props.rowData.asset_type_id)" 
                                    class="fa fa-file-video-o fa-2x text-light" 
                                    aria-hidden="true"
                                ></i>
                                <i v-else-if="assetTypesMap.videoPlaceholderTypes.includes(props.rowData.asset_type_id)" 
                                    class="fa fa-file-video-o fa-2x text-light" 
                                    aria-hidden="true"
                                ></i>
                                <i v-else class="fa fa-file-o fa-2x text-light" aria-hidden="true"></i>
                            </div>
                        </div>
                    </template>
                    <template slot="actions-slot" slot-scope="props">
                        <div class="d-flex align-items-center justify-content-end w-100">
                            <a :href="'/assets/' + props.rowData.id + '/edit'" class="btn btn-sm btn-primary">
                                View
                            </a>
                            <button class="btn btn-sm btn-danger ml-1" v-if="permissions.length && permissions.includes('delete_dam')" @click="handleDeleteAssetModal(props.rowData.id)">
                                Delete
                            </button>
                        </div>
                    </template>
                </vuetable>
                <div class="row mt-1">
                    <div class="col-md-6">
                        <base-pagination-info :css="css.table" ref="paginationInfo">
                        </base-pagination-info>
                    </div>
                    <div class="col-md-6">
                        <base-pagination ref="pagination"
                            @vuetable-pagination:change-page="onChangePage"
                        ></base-pagination>
                    </div>
                </div>
                <div v-if="selectedItems.length" class="bulk-select-prompt d-flex align-items-center justify-content-center px-3 py-2">
                    <div class="mr-4 pr-4" style="border-right: 1px solid #dedede;">
                        <strong>{{ selectedItems.length }}</strong> Selected
                    </div>
                    <div>
                        <div class="dropup channel-tool-menu">
                            <button data-id="bulk-actions-button" type="button" class="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center" data-toggle="dropdown">
                                <span>
                                    Actions
                                </span>
                                <i class="fa fa-cog ml-1" aria-hidden="true"></i>
                            </button>
                            <div class="dropdown-menu dropdown-menu-right" style="bottom: 100%;">
                                <button style="font-size: 0.8rem;" v-if="permissions.includes('delete_dam') && features.includes('dam_advanced')" @click="uiStates.bulkDeleteModal = true" class="dropdown-item d-flex align-items-center justify-content-between">
                                    <i style="font-size: 1rem;" class="fa fa-trash mr-2" aria-hidden="true"></i>
                                    <span>
                                        Delete
                                    </span>
                                </button>
                                <div class="dropdown-divider"  v-if="permissions.includes('delete_dam') && features.includes('dam_advanced')"></div>
                                <button style="font-size: 0.8rem;" 
                                        v-if="permissions.includes('edit_dam')" 
                                        @click="uiStates.bulkAssignModal = true" 
                                        class="dropdown-item d-flex align-items-center justify-content-between"
                                >
                                    <i style="font-size: 1rem;" class="fa fa-link mr-2" aria-hidden="true"></i>
                                    <span>Assign</span>
                                </button>
                                <div class="dropdown-divider"></div>
                                <button style="font-size: 0.8rem;" 
                                        v-if="features.includes('dam_advanced')"  
                                        @click="uiStates.bulkTagsModal = true" 
                                        class="dropdown-item d-flex align-items-center 
                                        justify-content-between"
                                >
                                    <i style="font-size: 1rem;" class="fa fa-tags mr-2" aria-hidden="true"></i>
                                    <span>Edit Tags</span>
                                </button>
                                <div class="dropdown-divider" v-if="features.includes('dam_advanced')"></div>
                                <button style="font-size: 0.8rem;" 
                                        v-if="selectedAreImages && features.includes('dam_advanced')" 
                                        @click="uiStates.bulkResizeModal = true" 
                                        class="dropdown-item d-flex align-items-center justify-content-between"
                                >
                                    <i style="font-size: 1rem;" class="fa fa-expand mr-2" aria-hidden="true"></i>
                                    <span>Resize</span>
                                </button>
                                <div class="dropdown-divider" v-if="selectedAreImages && features.includes('dam_advanced')"></div>
                                <button style="font-size: 0.8rem;" 
                                        v-if="selectedAreImages && features.includes('dam_advanced')" 
                                        @click="uiStates.bulkConvertModal = true" 
                                        class="dropdown-item d-flex align-items-center justify-content-between"
                                >
                                    <i style="font-size: 1rem;" class="fa fa-file-image-o mr-2" aria-hidden="true"></i>
                                    <span>Convert</span>
                                </button>
                                <div class="dropdown-divider" v-if="selectedAreImages && features.includes('dam_advanced')"></div>
                                <button style="font-size: 0.8rem;" 
                                        v-if="selectedAreImages && features.includes('dam_advanced')" 
                                        @click="uiStates.bulkWatermarkModal = true" 
                                        class="dropdown-item d-flex align-items-center justify-content-between"
                                >
                                    <i style="font-size: 1rem;" class="fa fa-sticky-note-o mr-2" aria-hidden="true"></i>
                                    <span>Watermark</span>
                                </button>
                                <div class="dropdown-divider" v-if="selectedAreImages && permissions.includes('edit_dam')"></div>
                                <button style="font-size: 0.8rem;" 
                                        v-if="permissions.includes('edit_dam')" 
                                        @click="uiStates.bulkFolderModal = true" 
                                        class="dropdown-item d-flex align-items-center justify-content-between"
                                >
                                    <i style="font-size: 1rem;" class="fa fa-folder-open mr-2" aria-hidden="true"></i>
                                    <span>Set Folder</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import BasePagination from "../tables/base-pagination.vue";
import BasePaginationInfo from "../tables/base-pagination-info.vue";
import SelectItemFromApiProvidedList from "../generic/select-item-from-api-provided-list.vue";
import SelectItemFromProvidedList from "../generic/select-item-from-provided-list.vue";
import SelectFolder from "./select-folder.vue";
import axios from 'axios';

const base_url = window.location.protocol + "//" + window.location.host;

export default {
    name: "list-view",
    components: {
        "base-pagination": BasePagination,
        "base-pagination-info": BasePaginationInfo,
        "select-item-from-provided-list": SelectItemFromProvidedList,
        "select-item-from-api-provided-list": SelectItemFromApiProvidedList,
        "select-folder": SelectFolder,
    },
    props: {
        currentFolder: {
            required: false,
            type: [Object, String]
        },
        root: {
            required: false,
            type: Boolean
        },
        filter: {
            required: false,
            type: String
        },
        paginate: {
            required: true,
            type: Number
        },
        permissions: {
            required: false,
            type: [Object, Array]
        },
        features: {
            required: false,
            type: [Object, Array]
        }
    },
    data() {
        return {
            perPage: this.paginate,
            linkLoading: false,
            loading: true,     
            isMounted: false, 
            uiStates: {
                createAssetModal: false,
                deleteAssetModal: false,
                bulkResizeModal: false,
                bulkDeleteModal: false,
                bulkAssignModal: false,
                bulkTagsModal: false,
                bulkConvertModal: false,
                bulkFolderModal: false,
                bulkWatermarkModal: false
            },
            selectedEntityId: null,
            selectedLinkType: 'Products',
            selectedItems: [],
            watermarkImage: '',
            watermarkPosition: '',
            watermarkSuffix: '',
            pendingTag: '',
            addTagsBulk: [],
            createAssetModal: {
                private: false,
            },
            deleteAssetModal: {
                assetId: null
            },
            moreParams: {},
            setFolder: 0,
            resizeHeight: 1080,
            resizeWidth: 1920,
            resizeAspectRatio: false,
            resizeAspectRatioPreserve: 'width',
            resizeUsePercent: false,
            resizePercent: 100,
            resizeQuality: 100,
            resizeSuffix: '',
            convertFormat: 'png',
            convertSuffix: '',
            fields: [
                {
                    name: "__slot:thumbnail-slot",
                    title: "Thumbnail",
                },  
                {
                    name: "name",
                    title: "Name",
                },
                {
                    name: "size_readable",
                    title: "Size",
                },
                {
                    name: "file_extension",
                    title: "File Type",
                },
                {
                    name: "created_at",
                    title: "Created",
                },
                {
                    name: "updated_at",
                    title: "Updated",
                },
                {
                    name: "__slot:actions-slot",
                    title: "",
                },
            ],
            css: {
                table: {
                    tableClass: "table table-middle table-minimal",
                    tableWrapper: "",
                    ascendingIcon: "sort-icon-asc",
                    descendingIcon: "sort-icon-desc",
                    infoClass: "",
                },
            },
        }
    },
    mounted() {
        this.isMounted = true;

        let date = new Date();
        date = date.toISOString().split('T')[0];

        this.resizeSuffix = `Resized ${date}`;
        this.convertSuffix = `Converted ${date}`;

        if (this.permissions.includes('edit_dam') || this.permissions.includes('delete_dam')) {
            this.fields.unshift({
                name: "__checkbox",
                titleClass: 'center aligned',
                dataClass: 'right aligned'
            });

            this.$refs.vuetable.normalizeFields();
        }
    },
    computed: {
        apiURL() {
            return this.root ? 
                `${base_url}/pimapi/assets/folder/0/assets` : 
                `${base_url}/pimapi/assets/folder/${this.currentFolder.id}/assets`;
        },
        assetTypesMap() {
            return {
                displayableImageTypes: [1, 2, 3, 4, 26],
                displayableVideoTypes: [28, 31],
                documentPlaceholderTypes: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                videoPlaceholderTypes: [27, 30, 32, 33, 34, 35, 36, 37, 38, 39],
                imagePlaceholderTypes: [24, 25],
                genericPlaceholderTypes: [16, 17, 18, 19, 20, 21, 22, 23, 40, 41]
            }
        },
        selectedMatchingTags() {
            const tags = {};
            
            this.selectedItems.forEach(item => {
                if (item.tags) {
                    const itemTags = item.tags.split(',');
                
                    itemTags.forEach((tag) => {
                        if (!tags[tag]) {
                            tags[tag] = [];
                        } 
                        
                        tags[tag].push({
                            id: item.id,
                            name: item.name
                        });
                    });
                }
            })

            return tags;
        },
        selectedAreImages() {
            return this.selectedItems.every((item) => {
                return ((item.asset_type_id >= 1 && item.asset_type_id <= 4) || item.asset_type_id === 26);
            });
        }
    },
    watch: {
        filter: function(value) {
            this.moreParams.filter = value;

            this.$nextTick( () => this.$refs.vuetable.refresh())
        },
        paginate: function(value) {
            this.perPage = value;

            this.$nextTick( () => this.$refs.vuetable.refresh())
        },
        uiStates: {
            handler: function() {
                let closed = Object.values(this.uiStates).every((modal) => modal === false);
                    
                if (closed) {
                    const scrollY = document.body.style.top;
                    document.body.style.position = 'initial';
                    document.body.style.top = '';
                    window.scrollTo(0, parseInt(scrollY || '0') * -1);  
                } else {
                    document.body.style.position = 'fixed';
                    document.body.style.top = `-${window.scrollY}px`;
                }
            },
            deep: true
        }
    },
    methods: {
        clearModals() {
            this.uiStates = {
                createAssetModal: false,
                deleteAssetModal: false,
                bulkResizeModal: false,
                bulkDeleteModal: false,
                bulkAssignModal: false,
                bulkTagsModal: false,
                bulkConvertModal: false,
                bulkFolderModal: false
            }
        },
        onChangePage(page) {
            this.$refs.vuetable.changePage(page);
        },
        onPaginationData(paginationData) {
            this.$refs.pagination.setPaginationData(paginationData);
            this.$refs.paginationInfo.setPaginationData(paginationData);
        },
        onLoaded: function () {
            this.loading = false;
        },
        onCheckbox(isChecked, row) {
            if (isChecked) {
                this.selectedItems.push(row);
            } else {
                this.selectedItems = this.selectedItems.filter(item => item.id !== row.id);
            }
        },
        onCheckboxAll(isChecked) {
            if (isChecked === false) {
                this.$refs.vuetable.selectedTo = [];
                this.selectedItems = [];
            } else {
                let selected = this.$refs.vuetable.tableData.map(item => item);
                this.selectedItems = this.selectedItems.concat(selected);
            }
        },
        handleAssetLightbox(asset) {
            this.$emit('lightbox-selected', asset);
        },
        handleDeleteAssetModal(assetId) {
            this.deleteAssetModal.assetId = assetId;
            this.uiStates.deleteAssetModal = true;
        },
        closeDeleteAssetModal() {
            this.deleteAssetModal.assetId = null;
            this.uiStates.deleteAssetModal = false;
        },
        addPendingTag() {
            let tag = this.pendingTag.trim();

            if (this.addTagsBulk.includes(tag) || Object.keys(this.selectedMatchingTags).includes(tag)) {
                this.$parent.$emit('alert', {
                    message: 'Tag exists already',
                    status: "alert-warning"
                });
            } else {
                if (tag.length) {
                    this.addTagsBulk.push(tag);
                }
            }

            this.pendingTag = '';
        },
        removePendingTag(tag) {
            this.addTagsBulk = this.addTagsBulk.filter((i) => i !== tag);
        },
        submitTagsBulk() {
            let selected = this.selectedItems.map((item) => item.id);
            let tags;

            if (this.addTagsBulk.length) {
                tags = this.addTagsBulk.join(',')
            } else {
                if (this.pendingTag.length) {
                    tags = this.pendingTag;
                }  
            }

            axios.post(base_url + '/pimapi/assets/bulk/tag/add', {
                selected: selected,
                tags: tags
            })
                .then(function(res) {
                    this.loading = false;
                    this.clearModals();
                    this.$parent.$emit('alert', {
                        message: 'Tags added',
                        status: "alert-success"
                    });

                    this.$refs.vuetable.refresh();
                }.bind(this))
                .catch(function(err) {
                    this.loading = false;
                    
                    this.$parent.$emit('alert', {
                        message: err.response.data.message,
                        status: "alert-danger"
                    });
                }.bind(this));
        },
        removeTagBulk(tag, tagItems) {
            tagItems = tagItems.map(item => item.id);

            axios.post(base_url + '/pimapi/assets/bulk/tag/remove', {
                selected: tagItems,
                tag: tag
            })
                .then(function(res) {
                    this.loading = false;
                    this.clearModals();
                    this.$parent.$emit('alert', {
                        message: 'Tag removed',
                        status: "alert-success"
                    });
                }.bind(this))
                .catch(function(err) {
                    this.loading = false;
                    
                    this.$parent.$emit('alert', {
                        message: err.response.data.message,
                        status: "alert-danger"
                    });
                }.bind(this));
        },
        submitSetFolder() {
            let selected = this.selectedItems.map((item) => item.id);

            axios.put(base_url + '/pimapi/assets/bulk/folder/' + this.setFolder + '/paste', {
                selected: selected
            })
                .then(function(res) {
                    this.loading = false;
                    this.clearModals();
                    this.$parent.$emit('alert', {
                        message: 'Folder set',
                        status: "alert-success"
                    });
                }.bind(this))
                .catch(function(err) {
                    this.loading = false;
                    
                    this.$parent.$emit('alert', {
                        message: err.response.data.message,
                        status: "alert-danger"
                    });
                }.bind(this));
        },
        submitConvert() {
            let selected = this.selectedItems.map((item) => item.id);

            axios.post(base_url + '/pimapi/assets/bulk/convert', {
                selected: selected,
                format: this.convertFormat,
                suffix: this.convertSuffix
            })
                .then(function(res) {
                    this.loading = false;
                    this.clearModals();
                    this.$parent.$emit('alert', {
                        message: 'Asset conversion process has started in the background, your assets will appear here shortly.',
                        status: "alert-success"
                    });
                }.bind(this))
                .catch(function(err) {
                    this.loading = false;
                    
                    this.$parent.$emit('alert', {
                        message: err.response.data.message,
                        status: "alert-danger"
                    });
                }.bind(this));
        },
        submitResize() {
            let selected = this.selectedItems.map((item) => item.id);
            
            let body = {
                quality: this.resizeQuality,
                selected: selected,
                suffix: this.resizeSuffix
            };

            if (this.resizeUsePercent) {
                body.percentage = this.resizePercent;
                this.resizeAspectRatio = false;
            } else {
                if (this.resizeAspectRatio) {
                    if (this.resizeAspectRatioPreserve === 'width') {
                        body.width = this.resizeWidth;
                    } else {
                        body.height = this.resizeHeight;
                    }
                } else {
                    body.width = this.resizeWidth;
                    body.height = this.resizeHeight;
                }
            }

            body.preserveAspectRatio = this.resizeAspectRatio;

            axios.post(base_url + '/pimapi/assets/bulk/resize', body)
                .then(function(res) {
                    this.loading = false;
                    this.clearModals();
                    this.$parent.$emit('alert', {
                        message: 'Asset resize process has started in the background, your assets will appear here shortly.',
                        status: "alert-success"
                    });
                }.bind(this))
                .catch(function(err) {
                    this.loading = false;
                    
                    this.$parent.$emit('alert', {
                        message: err.response.data.message,
                        status: "alert-danger"
                    });
                }.bind(this));
        },
        submitWatermark() {
            let formData = new FormData();
            let selected = this.selectedItems.map((item) => item.id);
            let file = this.$refs.watermark.files[0];

            formData.append('watermark', file);
            formData.append('assetIds', JSON.stringify(selected));
            formData.append('position', this.watermarkPosition);
            formData.append('suffix', this.watermarkSuffix);

            axios.post(base_url + '/pimapi/assets/bulk/watermark', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                })
                .then(function(res) {
                    this.loading = false;
                    this.clearModals();
                    this.$parent.$emit('alert', {
                        message: 'Asset transformation process has started in the background, your assets will appear here shortly.',
                        status: "alert-success"
                    });
                }.bind(this))
                .catch(function(err) {
                    this.loading = false;
                    
                    this.$parent.$emit('alert', {
                        message: err.response.data.message,
                        status: "alert-danger"
                    });
                }.bind(this));
        },
        confirmDeleteAsset() {
            this.loading = true;

            if (typeof this.deleteAssetModal.assetId != null) {
                axios.delete('/pimapi/assets/' + this.deleteAssetModal.assetId)
                    .then(function (response) {
                        this.deleteAssetModal.assetId = null;
                        this.clearModals();

                        this.$refs.vuetable.refresh();

                        this.loading = false;

                        this.$parent.$emit('alert', {
                            message: 'Success, asset deleted',
                            status: "alert-success"
                        });
                    }.bind(this))
                    .catch(function (error) {
                        this.loading = false;

                        this.$parent.$emit('alert', {
                            message: error.response.data.message,
                            status: "alert-danger"
                        });
                    }.bind(this));
            }
        },
        confirmBulkDelete() {
            this.loading = true;
            let selected = this.selectedItems.map((item) => item.id);

            axios.post(base_url + '/pimapi/assets/bulk/delete', {
                selected: selected,
            })
                .then(function(res) {
                    this.loading = false;
                    this.clearModals();

                    this.$parent.$emit('alert', {
                        message: 'Your assets are being deleted',
                        status: "alert-success"
                    });

                    this.$nextTick(() => {
                        this.selectedItems = [];
                        this.$refs.vuetable.selectedTo = [];
                        this.$refs.vuetable.refresh();
                    });
                }.bind(this))
                .catch(function(err) {
                    this.loading = false;
                    
                    this.$parent.$emit('alert', {
                        message: err.response.data.message,
                        status: "alert-danger"
                    });
                }.bind(this));
        },
        handleLinkSelection(value) {
            this.selectedEntity = value.id;
        },
        handleLinkAssets() {
            this.linkLoading = true;
            let selected = this.selectedItems.map((item) => item.id);

            axios.post(base_url + '/pimapi/assets/bulk/assign', {
                selected: selected,
                type: this.selectedLinkType,
                id: this.selectedEntity
            })
                .then(function(res) {
                    this.loading = false;
                    this.linkLoading = false;
                    this.clearModals();

                    this.$parent.$emit('alert', {
                        message: 'Assigned',
                        status: "alert-success"
                    });
                }.bind(this))
                .catch(function(err) {
                    this.loading = false;
                    this.linkLoading = false;
                    
                    this.$parent.$emit('alert', {
                        message: err.response.data.message,
                        status: "alert-danger"
                    });
                }.bind(this));
        },
        handleFolderSelection(value) {
            this.setFolder = value;
        }
    },
};
</script>
<style lang="scss" scoped>
.asset-items__thumbnail {
    width: 8rem;
    height: 8rem;
    object-fit: cover;
    border-radius: 2px;
}

.asset-preview__icon-outer {
    width: 8rem;
    height: 8rem;
    background: #333;
    overflow: hidden;
    position: relative;
    border-radius: 2px;
}

.asset-preview__icon-inner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.bulk-select-prompt {
    position: fixed;
    bottom: 4.5rem;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border-radius: 2px;
    border: 1px solid #dedede;
}

.asset-tag-cloud {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 0.25rem;
    min-width: 18rem;
    max-width: 26rem;
    width: 100%;

    button {
        cursor: pointer;
    }
}

.pending-tag {
    text-transform: none !important;
}
</style>

